import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";
import * as moment from "moment";
import { EventService } from "src/app/core/services/event.service";
import { ApiService } from "src/app/shared/services/api.service";

@Component({
  selector: "app-renew-detail",
  templateUrl: "./renew-detail.component.html",
  styleUrls: ["./renew-detail.component.scss"],
})
export class RenewDetailComponent implements OnInit {
  id: number;
  data: any = {};
  listDataLog: any[] = [];
  claim: any = null;
  role = JSON.parse(localStorage.getItem("currentUser"))["role"];

  submittedRefundRequest = false;
  submittedConfirmRefund = false;
  formRefundRequest: FormGroup;
  formConfirmRefund: FormGroup;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private modalService: NgbModal,
    private apiService: ApiService,
    private eventService: EventService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.id = this.activatedRoute.snapshot.params["id"];
    this.fetchData();
    this.fetchLogs();
    this.fetchClaims();
  }

  goBack() {
    this.router.navigate(["/renew-package"]);
  }

  fetchData() {
    this.eventService.setLoading(true);
    this.apiService.getList(`order-renew/${this.id}`).subscribe({
      next: (res: any) => {
        this.data = res?.metadata || {};
        this.eventService.setLoading(false);
      },
      error: () => {
        this.eventService.setLoading(false);
      },
    });
  }

  fetchLogs() {
    this.apiService
      .getList(`order-renew/${this.id}/logs?page=1&limit=100`)
      .subscribe({
        next: (res: any) => {
          this.listDataLog = res?.metadata?.results || [];
        },
      });
  }

  fetchClaims() {
    this.apiService.getList(`order-renew/${this.id}/claims`).subscribe({
      next: (res: any) => {
        this.claim = res?.metadata || null;
      },
    });
  }

  formatLogDateTime(value: string): { date: string; time: string } {
    if (!value) {
      return { date: "", time: "" };
    }
    const parsed = moment(value);
    if (!parsed.isValid()) {
      return { date: value, time: "" };
    }
    return { date: parsed.format("DD/MM/YYYY"), time: parsed.format("HH:mm") };
  }

  formatRefundDate(value: string): string {
    if (!value) {
      return "--";
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
  }

  get canCreateRefundRequest(): boolean {
    return !!this.claim?.order_claim_id && !this.claim?.order_cancel_id;
  }

  get canConfirmRefund(): boolean {
    return !!this.claim?.order_cancel_id && !this.claim?.refund_transaction_id;
  }

  openRefundRequestModal(content: any) {
    this.submittedRefundRequest = false;
    this.formRefundRequest = this.formBuilder.group({
      reason: ["", [Validators.required]],
    });
    this.modalService.open(content, {
      centered: true,
      scrollable: true,
      size: "lg",
    });
  }

  submitRefundRequest() {
    this.submittedRefundRequest = true;
    if (this.formRefundRequest.invalid) {
      return;
    }
    this.eventService.setLoading(true);
    this.apiService
      .postItem(`order-renew/refund-request`, {
        orderRenewId: this.id,
        orderClaimId: this.claim?.order_claim_id,
        reason: this.formRefundRequest.value.reason,
      })
      .subscribe({
        next: () => {
          this.eventService.setLoading(false);
          this.modalService.dismissAll();
          this.apiService.showToastMobile(
            this.translateService.instant("success"),
            this.translateService.instant("Tạo yêu cầu hoàn phí thành công")
          );
          this.fetchClaims();
          this.fetchLogs();
        },
        error: (err) => {
          this.eventService.setLoading(false);
          this.apiService.showToastMobile(
            "error",
            this.getErrorMessage(err) ||
              this.translateService.instant("Tạo yêu cầu hoàn phí thất bại")
          );
        },
      });
  }

  refundAmountDisplay = "";

  openConfirmRefundModal(content: any) {
    this.submittedConfirmRefund = false;
    this.refundAmountDisplay = "";
    this.formConfirmRefund = this.formBuilder.group({
      reason: ["", [Validators.required]],
      amount: [
        null,
        [
          Validators.required,
          Validators.min(1),
          Validators.max(this.data?.discounted_price || 0),
        ],
      ],
      ftCode: ["", [Validators.required]],
      refundDate: [null, [Validators.required]],
    });
    this.modalService.open(content, {
      centered: true,
      scrollable: true,
      size: "lg",
    });
  }

  onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleanValue = input.value.replace(/[^\d]/g, "");
    const numericValue = cleanValue ? Number(cleanValue) : null;
    this.refundAmountDisplay = numericValue !== null ? numericValue.toLocaleString("en-US") : "";
    input.value = this.refundAmountDisplay;
    this.formConfirmRefund.get("amount")?.setValue(numericValue);
  }

  submitConfirmRefund() {
    this.submittedConfirmRefund = true;
    if (this.formConfirmRefund.invalid) {
      return;
    }
    const formattedDate = this.formatLocalDate(
      this.formConfirmRefund.value.refundDate
    );
    Swal.fire({
      title: this.translateService.instant("FORM.Warning"),
      text: this.translateService.instant(
        "Bạn có chắc chắn đã hoàn phí cho khách hàng? (Hành động này sẽ bao gồm xuất hoá đơn thay thế nếu có hoá đơn gốc)"
      ),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "red",
      cancelButtonColor: "primary",
      confirmButtonText: this.translateService.instant("FORM.WarningConfirm"),
      cancelButtonText: this.translateService.instant("FORM.Cancel"),
    }).then((result) => {
      if (!result.value) {
        return;
      }
      this.eventService.setLoading(true);
      this.apiService
        .postItem(`order-renew/${this.id}/confirm-refund`, {
          amount: this.formConfirmRefund.value.amount,
          ftCode: this.formConfirmRefund.value.ftCode,
          reason: this.formConfirmRefund.value.reason,
          refundDate: formattedDate,
        })
        .subscribe({
          next: () => {
            this.eventService.setLoading(false);
            this.modalService.dismissAll();
            this.apiService.showToastMobile(
              this.translateService.instant("success"),
              this.translateService.instant("Xác nhận hoàn phí thành công")
            );
            this.fetchData();
            this.fetchClaims();
            this.fetchLogs();
          },
          error: (err) => {
            this.eventService.setLoading(false);
            this.apiService.showToastMobile(
              "error",
              this.getErrorMessage(err) ||
                this.translateService.instant("Xác nhận hoàn phí thất bại")
            );
          },
        });
    });
  }

  private getErrorMessage(err: any): string {
    if (!err) {
      return null;
    }
    if (typeof err === "string") {
      return err;
    }
    return err?.error?.message || err?.message || null;
  }

  private formatLocalDate(dateStr: string): string {
    if (!dateStr) {
      return null;
    }
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
}
